/**
 * cancelJob — API client for aborting active jobs.
 *
 * Maps job scope to the correct DELETE endpoint and sends the abort
 * request body (report flag + optional operator message).
 *
 * Package scope has two variants:
 *   - "package-user"  → DELETE /api/packages/jobs/:jobId      (user-owned, requireUser)
 *   - "package-admin" → DELETE /api/admin/packages/jobs/:jobId (admin-install surface)
 *
 * All other scopes route to the admin namespace.
 *
 * Throws a typed CancelJobError for 4xx/5xx so callers can surface
 * meaningful failure messages without try/catch boilerplate.
 */

import { getCsrfToken } from "../../lib/csrf.js";

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Which job subsystem + auth surface to abort against.
 * "package-user"  = user-facing route (requireUser + owner-or-admin check)
 * "package-admin" = admin-only route  (requireAdmin)
 */
export type JobScope =
  | "package-user"
  | "package-admin"
  | "vpn"
  | "ssl"
  | "update";

export type CancelJobBody = {
  report: boolean;
  message?: string;
};

/** Terminal status returned by the backend on a successful abort. */
export type AbortedStatus = "aborted" | "succeeded" | "failed";

export type CancelJobResult = {
  status: AbortedStatus;
};

/** Structured error so the confirm dialog can render a user-friendly message. */
export class CancelJobError extends Error {
  readonly statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.name = "CancelJobError";
    this.statusCode = statusCode;
  }
}

// ── URL map ───────────────────────────────────────────────────────────────────

const SCOPE_PATHS: Record<JobScope, string> = {
  "package-user":  "/api/packages/jobs",
  "package-admin": "/api/admin/packages/jobs",
  vpn:             "/api/admin/vpn/jobs",
  ssl:             "/api/admin/ssl/jobs",
  update:          "/api/admin/system/dashboard-update/jobs",
};

// ── Client ────────────────────────────────────────────────────────────────────

/**
 * Abort a running job.
 *
 * @param scope - Which subsystem + auth surface owns the job.
 * @param jobId - The job ID to abort.
 * @param body  - Whether to report + optional operator message.
 * @throws {CancelJobError} for 4xx/5xx responses.
 */
export async function cancelJob(
  scope: JobScope,
  jobId: string,
  body: CancelJobBody,
): Promise<CancelJobResult> {
  const csrfToken = getCsrfToken();

  const res = await fetch(`${SCOPE_PATHS[scope]}/${encodeURIComponent(jobId)}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let message = "Failed to cancel job";
    try {
      const err = await res.json() as { message?: string };
      if (typeof err.message === "string") {
        message = err.message;
      }
    } catch {
      // use default message
    }
    throw new CancelJobError(message, res.status);
  }

  const data = await res.json() as { ok: boolean; data?: { status?: string } };
  if (data.ok !== true || !data.data?.status) {
    throw new CancelJobError("Unexpected response from server", 500);
  }

  return { status: data.data.status as AbortedStatus };
}
